import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Send, Loader2, CheckCircle2, FileText, Clock, AlertCircle,
  User, Mail, Building2, Calendar, Target, DollarSign, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_INFO = {
  low: { label: 'Low', description: '10 business days', color: 'bg-slate-100 text-slate-700' },
  standard: { label: 'Standard', description: '7 business days', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', description: '5 business days', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', description: '72 hours (3 days)', color: 'bg-red-100 text-red-700' }
};

const PublicRequestForm = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    requester_name: '',
    requester_email: '',
    requester_department: '',
    title: '',
    request_type: '',
    business_unit: '',
    is_partner: false,
    partner_name: '',
    priority: 'standard',
    description: '',
    desired_due_date: '',
    target_audience: '',
    distribution_channels: [],
    campaign_initiative: '',
    budget_required: false,
    estimated_budget: '',
    compliance_review_required: false,
    event_date: '',
    form_data: {
      creative: {},
      website: {},
      events: {},
      social_media: {},
      email: {}
    }
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/requests/public/config`);
        setConfig(res.data);
      } catch (err) {
        setError('Failed to load form configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        marketing_support_type: formData.request_type,
        requester_email: formData.requester_email.toLowerCase(),
        estimated_budget: formData.budget_required && formData.estimated_budget 
          ? parseFloat(formData.estimated_budget) 
          : null,
        desired_due_date: formData.desired_due_date || null,
        event_date: formData.event_date || null,
        form_data: formData.form_data || null
      };

      const res = await axios.post(`${API_URL}/api/requests/public`, payload);
      setSubmitResult(res.data);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleChannel = (channel) => {
    setFormData(prev => ({
      ...prev,
      distribution_channels: prev.distribution_channels.includes(channel)
        ? prev.distribution_channels.filter(c => c !== channel)
        : [...prev.distribution_channels, channel]
    }));
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      form_data: {
        ...prev.form_data,
        [section]: {
          ...prev.form_data[section],
          [field]: value
        }
      }
    }));
  };

  const togglePlatform = (platform) => {
    const current = formData.form_data.social_media?.platforms || [];
    const updated = current.includes(platform)
      ? current.filter(p => p !== platform)
      : [...current, platform];
    updateFormData('social_media', 'platforms', updated);
  };

  const toggleEventAsset = (asset) => {
    const current = formData.form_data.events?.asset_needs || [];
    const updated = current.includes(asset)
      ? current.filter(a => a !== asset)
      : [...current, asset];
    updateFormData('events', 'asset_needs', updated);
  };

  const renderTypeSpecificSection = () => {
    switch (formData.request_type) {
      case 'Creative':
        return (
          <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200" data-testid="creative-section">
            <h3 className="font-semibold text-purple-800 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Creative Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Asset Type *</Label>
                <Select value={formData.form_data.creative?.asset_type || ''} onValueChange={(v) => updateFormData('creative', 'asset_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select asset type" /></SelectTrigger>
                  <SelectContent>
                    {(config?.creative_asset_types || ['Social Media Graphics', 'Email Banner', 'Flyer/Poster', 'Presentation', 'Infographic', 'Other']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Usage Type</Label>
                <Select value={formData.form_data.creative?.usage_type || ''} onValueChange={(v) => updateFormData('creative', 'usage_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select usage" /></SelectTrigger>
                  <SelectContent>
                    {(config?.creative_usage_types || ['Digital Only', 'Print Only', 'Both', 'Internal Use']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Goals / Requirements *</Label>
                <Textarea value={formData.form_data.creative?.goals_requirements || ''} onChange={(e) => updateFormData('creative', 'goals_requirements', e.target.value)} placeholder="What do you want to achieve with this creative?" rows={3} />
              </div>
              <div className="md:col-span-2">
                <Label>Design Specs</Label>
                <Textarea value={formData.form_data.creative?.design_specs || ''} onChange={(e) => updateFormData('creative', 'design_specs', e.target.value)} placeholder="Dimensions, colors, fonts, etc." rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Messaging / Copy</Label>
                <Textarea value={formData.form_data.creative?.messaging || ''} onChange={(e) => updateFormData('creative', 'messaging', e.target.value)} placeholder="Text/copy to include" rows={2} />
              </div>
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={formData.form_data.creative?.deadline || ''} onChange={(e) => updateFormData('creative', 'deadline', e.target.value)} />
              </div>
            </div>
          </div>
        );

      case 'Website':
        return (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="website-section">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Website Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Work Type *</Label>
                <Select value={formData.form_data.website?.work_type || ''} onValueChange={(v) => updateFormData('website', 'work_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select work type" /></SelectTrigger>
                  <SelectContent>
                    {(config?.website_work_types || ['New Page', 'Page Update', 'Bug Fix', 'Content Update', 'Design Change', 'Other']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>URL / Page</Label>
                <Input value={formData.form_data.website?.url_page || ''} onChange={(e) => updateFormData('website', 'url_page', e.target.value)} placeholder="https://..." />
              </div>
              <div className="md:col-span-2">
                <Label>Updates Needed *</Label>
                <Textarea value={formData.form_data.website?.updates_needed || ''} onChange={(e) => updateFormData('website', 'updates_needed', e.target.value)} placeholder="Describe the changes needed" rows={3} />
              </div>
              <div className="flex items-center gap-4">
                <Checkbox checked={formData.form_data.website?.copy_ready || false} onCheckedChange={(v) => updateFormData('website', 'copy_ready', v)} />
                <Label>Copy is ready</Label>
              </div>
              <div className="flex items-center gap-4">
                <Checkbox checked={formData.form_data.website?.requires_creative || false} onCheckedChange={(v) => updateFormData('website', 'requires_creative', v)} />
                <Label>Requires Creative Support</Label>
              </div>
              {formData.form_data.website?.copy_ready && (
                <div className="md:col-span-2">
                  <Label>Copy Content</Label>
                  <Textarea value={formData.form_data.website?.copy_content || ''} onChange={(e) => updateFormData('website', 'copy_content', e.target.value)} placeholder="Paste your copy here" rows={4} />
                </div>
              )}
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={formData.form_data.website?.deadline || ''} onChange={(e) => updateFormData('website', 'deadline', e.target.value)} />
              </div>
            </div>
          </div>
        );

      case 'Events':
        return (
          <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="events-section">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Event Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Event Name *</Label>
                <Input value={formData.form_data.events?.event_name || ''} onChange={(e) => updateFormData('events', 'event_name', e.target.value)} placeholder="Name of the event" />
              </div>
              <div>
                <Label>Event Date *</Label>
                <Input type="date" value={formData.form_data.events?.event_date || ''} onChange={(e) => updateFormData('events', 'event_date', e.target.value)} />
              </div>
              <div>
                <Label>Venue</Label>
                <Input value={formData.form_data.events?.venue || ''} onChange={(e) => updateFormData('events', 'venue', e.target.value)} placeholder="Venue name" />
              </div>
              <div>
                <Label>Venue Address</Label>
                <Input value={formData.form_data.events?.venue_address || ''} onChange={(e) => updateFormData('events', 'venue_address', e.target.value)} placeholder="Full address" />
              </div>
              <div>
                <Label>Number of Guests</Label>
                <Input type="number" value={formData.form_data.events?.number_of_guests || ''} onChange={(e) => updateFormData('events', 'number_of_guests', parseInt(e.target.value) || '')} />
              </div>
              <div>
                <Label>PH Attendees</Label>
                <Input value={formData.form_data.events?.ph_attendees || ''} onChange={(e) => updateFormData('events', 'ph_attendees', e.target.value)} placeholder="Names of attending staff" />
              </div>
              <div className="md:col-span-2">
                <Label>Messaging</Label>
                <Textarea value={formData.form_data.events?.messaging || ''} onChange={(e) => updateFormData('events', 'messaging', e.target.value)} placeholder="Key messaging for event" rows={2} />
              </div>
              <div>
                <Label>Deliverables Deadline</Label>
                <Input type="date" value={formData.form_data.events?.deliverables_deadline || ''} onChange={(e) => updateFormData('events', 'deliverables_deadline', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Asset Needs (Select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(config?.event_asset_types || ['Email Invitation', 'Social Media Posts', 'Landing Page', 'Flyer/Poster', 'Signage', 'Presentation', 'Other']).map(asset => (
                    <Button key={asset} type="button" variant={(formData.form_data.events?.asset_needs || []).includes(asset) ? 'default' : 'outline'} size="sm" onClick={() => toggleEventAsset(asset)}>
                      {asset}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'Social Media':
        return (
          <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="social-media-section">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <Send className="w-4 h-4" /> Social Media Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Platforms * (Select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(config?.social_platforms || ['Facebook', 'Instagram', 'LinkedIn', 'Twitter/X', 'TikTok', 'YouTube']).map(platform => (
                    <Button key={platform} type="button" variant={(formData.form_data.social_media?.platforms || []).includes(platform) ? 'default' : 'outline'} size="sm" onClick={() => togglePlatform(platform)}>
                      {platform}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Content Type</Label>
                <Select value={formData.form_data.social_media?.content_type || ''} onValueChange={(v) => updateFormData('social_media', 'content_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {['Single Post', 'Carousel', 'Story', 'Reel/Video', 'Series/Campaign'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Goal</Label>
                <Input value={formData.form_data.social_media?.goal || ''} onChange={(e) => updateFormData('social_media', 'goal', e.target.value)} placeholder="Awareness, engagement, etc." />
              </div>
              <div className="md:col-span-2">
                <Label>Message / Copy</Label>
                <Textarea value={formData.form_data.social_media?.message || ''} onChange={(e) => updateFormData('social_media', 'message', e.target.value)} placeholder="What do you want to say?" rows={3} />
              </div>
              <div>
                <Label>Call to Action</Label>
                <Input value={formData.form_data.social_media?.cta || ''} onChange={(e) => updateFormData('social_media', 'cta', e.target.value)} placeholder="Learn more, Sign up, etc." />
              </div>
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={formData.form_data.social_media?.deadline || ''} onChange={(e) => updateFormData('social_media', 'deadline', e.target.value)} />
              </div>
              <div className="flex items-center gap-4">
                <Checkbox checked={formData.form_data.social_media?.requires_creative || false} onCheckedChange={(v) => updateFormData('social_media', 'requires_creative', v)} />
                <Label>Requires Creative Support</Label>
              </div>
            </div>
          </div>
        );

      case 'Email':
        return (
          <div className="space-y-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="email-section">
            <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email Campaign Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Campaign Name *</Label>
                <Input value={formData.form_data.email?.campaign_name || ''} onChange={(e) => updateFormData('email', 'campaign_name', e.target.value)} placeholder="Campaign name" />
              </div>
              <div>
                <Label>Objective</Label>
                <Input value={formData.form_data.email?.objective || ''} onChange={(e) => updateFormData('email', 'objective', e.target.value)} placeholder="Goal of the email" />
              </div>
              <div className="md:col-span-2">
                <Label>Audience / Segmentation</Label>
                <Textarea value={formData.form_data.email?.audience_segmentation || ''} onChange={(e) => updateFormData('email', 'audience_segmentation', e.target.value)} placeholder="Who should receive this?" rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Messaging / Content</Label>
                <Textarea value={formData.form_data.email?.messaging || ''} onChange={(e) => updateFormData('email', 'messaging', e.target.value)} placeholder="Key messages and content" rows={3} />
              </div>
              <div>
                <Label>Call to Action</Label>
                <Input value={formData.form_data.email?.cta || ''} onChange={(e) => updateFormData('email', 'cta', e.target.value)} placeholder="Primary CTA" />
              </div>
              <div>
                <Label>Links & Assets</Label>
                <Input value={formData.form_data.email?.links_assets || ''} onChange={(e) => updateFormData('email', 'links_assets', e.target.value)} placeholder="URLs, attachments, etc." />
              </div>
              <div>
                <Label>Timeline / Cadence</Label>
                <Input value={formData.form_data.email?.timeline_cadence || ''} onChange={(e) => updateFormData('email', 'timeline_cadence', e.target.value)} placeholder="When and how often?" />
              </div>
              <div>
                <Label>Success Metrics</Label>
                <Input value={formData.form_data.email?.success_metrics || ''} onChange={(e) => updateFormData('email', 'success_metrics', e.target.value)} placeholder="How will you measure success?" />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={formData.form_data.email?.notes || ''} onChange={(e) => updateFormData('email', 'notes', e.target.value)} placeholder="Any additional notes" rows={2} />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (submitted && submitResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted!</h2>
            <p className="text-slate-600 mb-6">{submitResult.message}</p>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium">Request ID</span>
              </div>
              <code className="text-sm bg-white px-3 py-2 rounded border block overflow-x-auto">
                {submitResult.request_id}
              </code>
              <p className="text-xs text-slate-500 mt-2">
                Save this ID to check your request status
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mb-6">
              <Clock className="w-4 h-4" />
              <span>Expected response by: {new Date(submitResult.estimated_response).toLocaleDateString()}</span>
            </div>

            <Button 
              onClick={() => {
                setSubmitted(false);
                setSubmitResult(null);
                setFormData({
                  requester_name: '',
                  requester_email: '',
                  requester_department: '',
                  title: '',
                  request_type: '',
                  business_unit: '',
                  is_partner: false,
                  partner_name: '',
                  priority: 'standard',
                  description: '',
                  desired_due_date: '',
                  target_audience: '',
                  distribution_channels: [],
                  campaign_initiative: '',
                  budget_required: false,
                  estimated_budget: '',
                  compliance_review_required: false,
                  event_date: '',
                  form_data: {
                    creative: {},
                    website: {},
                    events: {},
                    social_media: {},
                    email: {}
                  }
                });
              }}
              variant="outline"
              className="w-full"
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/players-health-logo.png" 
              alt="Players Health" 
              className="h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Marketing Request Form
          </h1>
          <p className="text-slate-600">
            Submit your marketing request and our team will review it promptly.
          </p>
        </div>

        {/* Form */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-lg">Request Details</CardTitle>
            <CardDescription>
              Fields marked with * are required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Submission Error</p>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              )}

              {/* Your Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Your Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="requester_name">Your Name *</Label>
                    <Input
                      id="requester_name"
                      value={formData.requester_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                      placeholder="John Smith"
                      required
                      data-testid="requester-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="requester_email">Your Email *</Label>
                    <Input
                      id="requester_email"
                      type="email"
                      value={formData.requester_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_email: e.target.value }))}
                      placeholder="john@playershealth.com"
                      required
                      data-testid="requester-email-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="requester_department">Your Department</Label>
                    <Input
                      id="requester_department"
                      value={formData.requester_department}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                      placeholder="Sales, Product, Customer Success, etc."
                    />
                  </div>
                </div>
              </div>

              <hr className="border-slate-200" />

              {/* Request Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Request Information
                </h3>

                <div>
                  <Label htmlFor="title">Request Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Brief description of what you need"
                    required
                    data-testid="request-title-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="request_type">Marketing Support Type *</Label>
                    <Select 
                      value={formData.request_type} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, request_type: v }))}
                      required
                    >
                      <SelectTrigger data-testid="request-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(config?.marketing_support_types || config?.request_types)?.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="business_unit">Business Unit *</Label>
                    <Select 
                      value={formData.business_unit} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, business_unit: v }))}
                      required
                    >
                      <SelectTrigger data-testid="business-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {config?.business_units?.map(unit => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Checkbox 
                    id="is_partner" 
                    checked={formData.is_partner} 
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_partner: v }))} 
                  />
                  <Label htmlFor="is_partner" className="cursor-pointer">Is this for a Partner?</Label>
                </div>

                {formData.is_partner && (
                  <div>
                    <Label>Partner Name</Label>
                    <Input 
                      value={formData.partner_name} 
                      onChange={(e) => setFormData(prev => ({ ...prev, partner_name: e.target.value }))} 
                      placeholder="Partner organization name" 
                    />
                  </div>
                )}

                <div>
                  <Label>Priority Level</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {Object.entries(PRIORITY_INFO).map(([key, info]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, priority: key }))}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          formData.priority === key 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`priority-${key}`}
                      >
                        <Badge className={info.color}>{info.label}</Badge>
                        <p className="text-xs text-slate-500 mt-1">{info.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Detailed Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Please provide as much detail as possible about what you need, including goals, key messages, and any specific requirements..."
                    rows={5}
                    required
                    data-testid="description-input"
                  />
                </div>
              </div>

              {/* Conditional Sections based on request_type */}
              {renderTypeSpecificSection()}

              <hr className="border-slate-200" />

              {/* Additional Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Additional Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="desired_due_date">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Desired Due Date
                    </Label>
                    <Input
                      id="desired_due_date"
                      type="date"
                      value={formData.desired_due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, desired_due_date: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="event_date">Event Date (if applicable)</Label>
                    <Input
                      id="event_date"
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="target_audience">Target Audience</Label>
                  <Input
                    id="target_audience"
                    value={formData.target_audience}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="Who is this for? (e.g., Sports administrators, coaches, parents)"
                  />
                </div>

                <div>
                  <Label>Distribution Channels</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {config?.distribution_channels?.map(channel => (
                      <Button
                        key={channel}
                        type="button"
                        variant={formData.distribution_channels.includes(channel) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleChannel(channel)}
                      >
                        {channel}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="campaign_initiative">Related Campaign / Initiative</Label>
                  <Input
                    id="campaign_initiative"
                    value={formData.campaign_initiative}
                    onChange={(e) => setFormData(prev => ({ ...prev, campaign_initiative: e.target.value }))}
                    placeholder="If this is part of a larger campaign"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="budget_required"
                      checked={formData.budget_required}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, budget_required: v }))}
                    />
                    <Label htmlFor="budget_required" className="cursor-pointer">
                      <DollarSign className="w-3 h-3 inline mr-1" />
                      Budget Required
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="compliance_review"
                      checked={formData.compliance_review_required}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, compliance_review_required: v }))}
                    />
                    <Label htmlFor="compliance_review" className="cursor-pointer">
                      Compliance Review Required
                    </Label>
                  </div>
                </div>

                {formData.budget_required && (
                  <div>
                    <Label htmlFor="estimated_budget">Estimated Budget ($)</Label>
                    <Input
                      id="estimated_budget"
                      type="number"
                      value={formData.estimated_budget}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimated_budget: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="pt-4 border-t">
                <Button 
                  type="submit" 
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                  disabled={submitting || !formData.requester_name || !formData.requester_email || !formData.title || !formData.request_type || !formData.business_unit || !formData.description}
                  data-testid="submit-request-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
                <p className="text-xs text-slate-500 text-center mt-3">
                  You'll receive a confirmation with your request ID to track status
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Questions? Contact the Marketing team at marketing@playershealth.com
        </p>
      </div>
    </div>
  );
};

export default PublicRequestForm;
